import { defineStore } from 'pinia';
import { USER_STORE_KEY } from '@/constants/storeKeys';

interface State {
  api_token: string | null;
  user: object | null;
  email: string;
  subject_number: string;
  institution_id: number;
  remember: boolean;
  two_factor_data: {
    code_length: number;
    expire_minutes: number;
    is_enabled: boolean;
  }
  limit: number;
  last_view_update: number;
  showed_e_learnings: Array<{ [key: string]: string }>;
  manual_url: string | null;
  suspended_at: string | null;
  epro_enabled: boolean;
  econsent_enabled: boolean;
  allow_answer_without_subject_number: boolean;
  allow_subject_view_past_answers: boolean;
  allow_temporary_save: boolean;
  has_password_reset: boolean;
  is_websurvey: boolean;
  // 署名付きURLからのワンクリックホームログイン経由かどうか（パスワードを持たないユーザーの導線制御に使用）
  is_login_home: boolean;
  skip_email_registration: boolean;
}

// 状態定義を関数として抽出
const createInitialState = (): State => ({
  api_token: null,
  user: null,
  institution_id: 0,
  email: '',
  subject_number: '',
  remember: false,
  two_factor_data: {
    code_length: 0,
    expire_minutes: 0,
    is_enabled: false,
  },
  limit: 0,
  last_view_update: 0,
  showed_e_learnings: [],
  manual_url: null,
  suspended_at: null,
  epro_enabled: false,
  econsent_enabled: false,
  allow_answer_without_subject_number: false,
  allow_subject_view_past_answers: false,
  allow_temporary_save: false,
  has_password_reset: true,
  is_websurvey: false,
  is_login_home: false,
  skip_email_registration: false,
});

export const useUserStore = defineStore(USER_STORE_KEY, {
  state: (): State => createInitialState(),

  getters: {
    showELearning: (state) => {
      if (state.user == null) {
        return false;
      }
      const user = state.user as any;

      // サーバー側でeラーニング完了済み（e_learning_completed_at）が記録されている場合は表示しない
      if (user.subject_information?.e_learning_completed_at) {
        return false;
      }

      let result = true;
      state.showed_e_learnings.forEach((showedELearning) => {
        if (showedELearning['userId'] == user.id && showedELearning['projectId'] == user.project_id) {
          result = false;
        }
      });

      return result;
    },
  },

  actions: {
    addShowedELearning(): void {
      if (this.user == null) {
        return;
      }
      const user = this.user as any;
      let result = true;
      this.showed_e_learnings.forEach((showedELearning) => {
        if (showedELearning['userId'] == user.id && showedELearning['projectId'] == user.project_id) {
          result = false;
        }
      });

      if (result) {
        this.showed_e_learnings.push({ userId: user.id, projectId: user.project_id });
      }
    },

    updateUserInfo(userInfo: any): void {
      this.user = userInfo;
      this.email = userInfo.email;
      this.subject_number = userInfo.subject_information?.subject_number || '';
      this.manual_url = userInfo.manual_url;
      this.suspended_at = userInfo.suspended_at;
      this.epro_enabled = userInfo.epro_enabled;
      this.econsent_enabled = userInfo.econsent_enabled;
      this.allow_answer_without_subject_number = userInfo.allow_answer_without_subject_number ?? false;
      this.allow_subject_view_past_answers = userInfo.allow_subject_view_past_answers;
      this.allow_temporary_save = userInfo.allow_temporary_save ?? false;
    },

    // 初期状態を取得する関数
    getInitialState(): State {
      // 直接state関数を呼び出し
      return createInitialState();
    },

    // 保持したい状態のキーを定義
    getPreservedStateKeys(): (keyof State)[] {
      return ['showed_e_learnings', 'last_view_update'];
    },

    // $resetをオーバーライド
    $reset(): void {
      const preservedKeys = this.getPreservedStateKeys();
      const preservedState: Partial<State> = {};

      // 保持したい状態を動的に保存
      preservedKeys.forEach(key => {
        preservedState[key] = this[key] as State[typeof key];
      });

      // 初期状態を取得
      const initialState = this.getInitialState();

      // 状態をリセット（保持したい状態は除外）
      this.$patch({
        ...initialState,
        ...preservedState,
      });
    },

    // 完全リセット（デバッグ用など、必要に応じて）
    fullReset(): void {
      // 直接初期状態を設定（$resetは呼ばない）
      const initialState = this.getInitialState();
      this.$patch(initialState);
    },
  },

  persist: true,
});
